import {
  Component,
  Input,
  OnInit,
  OnDestroy,
  ElementRef,
  EventEmitter,
  Output,
  inject,
} from '@angular/core';
import {
  FilterService,
  SinglePopupService,
  PopupCloseEvent,
} from '@progress/kendo-angular-grid';
import {
  KENDO_DATEPICKER,
  PopupSettings,
} from '@progress/kendo-angular-dateinputs';
import {
  FilterDescriptor,
  CompositeFilterDescriptor,
} from '@progress/kendo-data-query';
import { addDays } from '@progress/kendo-date-math';
import { Subscription } from 'rxjs';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

const closest = (
  node: HTMLElement,
  predicate: (node: HTMLElement) => boolean
): HTMLElement => {
  while (node && !predicate(node)) {
    node = node.parentNode as HTMLElement;
  }

  return node;
};

@Component({
  selector: 'app-date-range-filter',
  templateUrl: './date-range-filter.component.html',
  styles: [
    `
      .k-form {
        padding: 5px;
      }
    `,
  ],
  imports: [KENDO_DATEPICKER, CommonModule, FormsModule],
})
export class DateRangeFilterComponent implements OnInit, OnDestroy {
  private element = inject(ElementRef);
  private popupService = inject(SinglePopupService);

  @Input() public filter: CompositeFilterDescriptor;
  @Input() public filterService: FilterService;
  @Input() public field: string;
  @Output() updateFilter = new EventEmitter();

  public start: Date;
  public end: Date;

  public get min(): Date {
    return this.start ? addDays(this.start, 1) : null;
  }

  public get max(): Date {
    return this.end ? addDays(this.end, -1) : null;
  }

  public popupSettings: PopupSettings = {
    popupClass: 'date-range-filter',
  };

  private popupSubscription: Subscription;

  constructor() {
    const popupService = this.popupService;

    // Handle the service onClose event and prevent the menu from closing when the datepickers are still active.
    this.popupSubscription = popupService.onClose.subscribe(
      (e: PopupCloseEvent) => {
        if (
          document.activeElement &&
          closest(
            document.activeElement as HTMLElement,
            node =>
              node === this.element.nativeElement ||
              String(node.className).indexOf('date-range-filter') >= 0
          )
        ) {
          e.preventDefault();
        }
      }
    );
  }

  public ngOnInit(): void {
    this.start = this.findValue('start');
    this.end = this.findValue('end');
  }

  public ngOnDestroy(): void {
    this.popupSubscription.unsubscribe();
  }

  public onStartChange(value: Date): void {
    this.filterRange(value, this.end);
  }

  public onEndChange(value: Date): void {
    this.filterRange(this.start, value);
  }

  private findValue(operator) {
    const filter = this.filter.filters.filter(
      x =>
        (x as FilterDescriptor).field === this.field &&
        (x as FilterDescriptor).operator === operator
    )[0];
    return filter ? (filter as FilterDescriptor).value : null;
  }

  private filterRange(start, end) {
    const filters = [];

    if (start && (!end || start < end)) {
      filters.push({
        field: this.field,
        operator: 'start',
        value: start,
      });
      this.start = start;
    }

    if (end && (!start || start < end)) {
      filters.push({
        field: this.field,
        operator: 'end',
        value: end,
      });
      this.end = end;
    }

    this.filterService.filter({
      logic: 'and',
      filters: filters,
    });
    this.updateFilter.emit(this.field);
  }
}
